import React, { useState, useEffect } from "react";
import Section from "./layouts/Section";
import { SiteWide } from "./customComponents";
import PaginatedItems from "./customComponents/PaginatedItems";

import { useSelector, useDispatch } from 'react-redux';
import { chattyActions } from '../redux/actions';
import { homeScreenStatSelect } from '../redux/selectors/index';
import { notify, money } from '../utility/Helper1';
import { useNavigate } from "react-router-dom";
import APIRequests from '../utility/APIRequests';

import {
    TrendingUp,
    DollarSign,
    Clock,
    Download,
    MessageSquare,
    ChevronDown,
    ChevronUp,
    CheckCircle,
    Layers
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// Transcript: sources chat sessions from chatty_centers + chatty_messages
// ─────────────────────────────────────────────────────────────────────────────

function Transcript() {
    const finance = useSelector(homeScreenStatSelect);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('');

    // All chat sessions sourced from chatty_centers (unfiltered)
    const [allChattySessions, setAllChattySessions] = useState([]);
    const [loadingChats, setLoadingChats] = useState(true);

    // UI states
    const [activeTab, setActiveTab] = useState("growth"); // "growth" | "activity"
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [expandedLedgerId, setExpandedLedgerId] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [weeklyActivity, setWeeklyActivity] = useState({
        Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
    });
    // Per-session ledger messages
    const [ledgerMessages, setLedgerMessages] = useState({}); // { txn_id: [...messages] }
    const [ledgerEarnings, setLedgerEarnings] = useState({}); // { txn_id: [...earnings] }
    const [loadingLedgerId, setLoadingLedgerId] = useState(null);

    const type = finance?.[0]?.type;

    // Active currency — the selector always puts the selected currency first
    const activeCurrencyType = type ?? localStorage.getItem('activeCurrencyType') ?? '148';

    // Only show sessions belonging to the currently active currency
    const chattySessions = allChattySessions.filter(
        s => String(s.currency) === String(activeCurrencyType)
    );

    // ── Fetch chatty sessions ────────────────────────────────────────────────
    const fetchChattySessions = (showLoading = true) => {
        if (showLoading) setLoadingChats(true);
        dispatch(chattyActions.chattyLoad((res) => {
            setLoadingChats(false);
            if (res && res.code === 1) {
                setAllChattySessions(res.data.chats || []);
            }
        }));
    };

    // ── Fetch weekly chat activity ───────────────────────────────────────────
    const fetchActivity = async () => {
        try {
            const api = new APIRequests();
            const res = await api.runGet("chatty/weekly-activity");
            if (res && res.code === 1 && res.data && res.data.activity) {
                setWeeklyActivity(res.data.activity);
            }
        } catch (err) {
            console.error("Error fetching weekly activity:", err);
        }
    };

    useEffect(() => {
        fetchChattySessions();
        fetchActivity();
        const interval = setInterval(fetchChattySessions.bind(null, false), 30000);
        return () => clearInterval(interval);
    }, []);

    // ── Aggregates ───────────────────────────────────────────────────────────
    const activeSessions = chattySessions.filter(s => Number(s.active) === 1);
    const allProfits = chattySessions.reduce((a, b) => a + Number(b?.profit || 0), 0);

    // ── Growth Ledger: fetch earnings for a session and build day-by-day payments ──
    const toggleLedger = (session) => {
        const sid = session.txn_id;
        if (expandedLedgerId === sid) {
            setExpandedLedgerId(null);
            return;
        }
        setExpandedLedgerId(sid);

        // Only load if not already cached
        if (ledgerEarnings[sid]) return;

        setLoadingLedgerId(sid);
        dispatch(chattyActions.chattyGetEarnings(session.id, (res) => {
            setLoadingLedgerId(null);
            if (res && res.code === 1 && res.data) {
                setLedgerEarnings(prev => ({ ...prev, [sid]: res.data.earnings || [] }));
            }
        }));
    };

    // Build ledger from ONLY the real earned days stored in DB
    const generateChattyLedger = (session) => {
        const earnings = ledgerEarnings[session.txn_id] || [];
        const capital = Number(session.capital) || 0;

        let cumulativeProfit = 0;
        return earnings.map((row, idx) => {
            const amount = Number(row.amount) || 0;
            cumulativeProfit += amount;

            const dateKey = row.earnedDate || "";
            // Parse YYYY-MM-DD manually to avoid timezone / browser format issues
            const parts = dateKey.split('-');
            const dateObj = parts.length === 3
                ? new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
                : null;
            const dateLabel = dateObj && !isNaN(dateObj)
                ? dateObj.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })
                : dateKey; // fall back to raw string if still unparseable

            return {
                entryNum: idx + 1,
                date: dateLabel,
                dateKey,
                earned: amount,
                cumulative: parseFloat((capital + cumulativeProfit).toFixed(4))
            };
        });
    };

    // ── Download: all chat messages for a session ────────────────────────────
    const handleDownloadTranscript = (session) => {
        setDownloadingId(session.id);

        dispatch(chattyActions.chattyGetMessages(session.id, (res) => {
            setDownloadingId(null);

            if (res && res.code === 1 && res.data && res.data.messages) {
                const messagesList = res.data.messages || [];
                const botName = session.chatName || "Chat Partner";

                // Build the transcript text
                let txt = `==================================================\n`;
                txt += `          AI CHAT TRANSCRIPT & EARNINGS LOG\n`;
                txt += `==================================================\n`;
                txt += `Session ID    : ${session.txn_id}\n`;
                txt += `Chat Partner  : ${botName}\n`;
                txt += `Status        : ${Number(session.active) === 1 ? "ACTIVE" : "COMPLETED"}\n`;
                txt += `Capital       : ${session.capital} ${session.currencyAbbr}\n`;
                txt += `Daily ROI     : ${session.dailyRoi}%\n`;
                txt += `Total Profit  : ${session.profit} ${session.currencyAbbr}\n`;
                txt += `Chat Days     : ${session.daysEarned} / ${session.tenure}\n`;
                txt += `Session Start : ${session.timeCreated}\n`;
                txt += `Generated On  : ${new Date().toLocaleString()}\n`;
                txt += `==================================================\n\n`;

                txt += `PAYMENT LEDGER:\n`;
                txt += `--------------------------------------------------\n`;
                const dailyProfit = parseFloat(((Number(session.dailyRoi) / 100) * Number(session.capital)).toFixed(4));
                txt += `Daily earning rate : ${dailyProfit} ${session.currencyAbbr} (per chat day)\n`;
                txt += `Total earned so far: ${session.profit} ${session.currencyAbbr}\n`;
                txt += `--------------------------------------------------\n\n`;

                txt += `CHAT CONVERSATIONS:\n`;
                txt += `--------------------------------------------------\n`;

                if (messagesList.length === 0) {
                    txt += `[No messages in this chat session yet.]\n`;
                } else {
                    messagesList.forEach((msg) => {
                        const sender = msg.role === "user" ? "YOU" : botName.toUpperCase();
                        txt += `[${msg.timeCreated || ""}] ${sender}:\n`;
                        txt += `  "${msg.message}"\n`;
                        txt += `--------------------------------------------------\n`;
                    });
                }

                txt += `\n==================================================\n`;
                txt += `           END OF EXPORTED TRANSCRIPT\n`;
                txt += `==================================================\n`;

                const blob = new Blob([txt], { type: 'text/plain;charset=utf-8' });
                const url = URL.createObjectURL(blob);
                const el = document.createElement("a");
                el.href = url;
                el.download = `transcript_${botName.toLowerCase().replace(/\s+/g, "_")}_${session.txn_id.slice(0, 8)}.txt`;
                document.body.appendChild(el);
                el.click();
                document.body.removeChild(el);
                URL.revokeObjectURL(url);

                notify("Chat transcript exported successfully!", 1);
            } else {
                notify("No messages found for this session.", 0);
            }
        }));
    };

    // ── Chart Data ───────────────────────────────────────────────────────────
    // Growth chart: cumulative profit across the week (based on daysEarned)
    const totalDaysEarned = chattySessions.reduce((a, b) => a + Number(b.daysEarned || 0), 0);
    const chartGrowthData = [
        { label: "Mon", value: allProfits * 0.12 },
        { label: "Tue", value: allProfits * 0.28 },
        { label: "Wed", value: allProfits * 0.45 },
        { label: "Thu", value: allProfits * 0.62 },
        { label: "Fri", value: allProfits * 0.78 },
        { label: "Sat", value: allProfits * 0.90 },
        { label: "Sun", value: allProfits }
    ];

    const chartActivityData = [
        { label: "Mon", value: weeklyActivity.Mon || 0 },
        { label: "Tue", value: weeklyActivity.Tue || 0 },
        { label: "Wed", value: weeklyActivity.Wed || 0 },
        { label: "Thu", value: weeklyActivity.Thu || 0 },
        { label: "Fri", value: weeklyActivity.Fri || 0 },
        { label: "Sat", value: weeklyActivity.Sat || 0 },
        { label: "Sun", value: weeklyActivity.Sun || 0 }
    ];

    const activeDataset = activeTab === "growth" ? chartGrowthData : chartActivityData;
    const maxVal = Math.max(...activeDataset.map(d => d.value), 1);

    const points = activeDataset.map((d, i) => {
        const x = 30 + i * (440 / 6);
        const y = 160 - (d.value / maxVal) * 120;
        return { x, y, value: d.value, label: d.label };
    });

    const polylineStr = points.map(p => `${p.x},${p.y}`).join(" ");

    // ────────────────────────────────────────────────────────────────────────
    // Session Card
    // ────────────────────────────────────────────────────────────────────────
    function SessionCardTemplate({ currentItems }) {
        return (
            <>
                {currentItems && currentItems.map((session) => {
                    const isLedgerOpen = expandedLedgerId === session.txn_id;
                    const isActive = Number(session.active) === 1 && !session.isExpired;
                    const dailyProfit = parseFloat(((Number(session.dailyRoi) / 100) * Number(session.capital)).toFixed(4));
                    const progress = session.tenure > 0 ? Math.round((session.daysEarned / session.tenure) * 100) : 0;
                    const ledger = isLedgerOpen ? generateChattyLedger(session) : [];
                    const isLoadingThisLedger = loadingLedgerId === session.txn_id;

                    return (
                        <div key={session.txn_id} style={{
                            background: "var(--card-bg, #ffffff)",
                            border: "1px solid var(--card-border, rgba(59,130,246,0.08))",
                            boxShadow: "var(--card-shadow, 0 4px 18px rgba(59,130,246,0.04))",
                            borderRadius: 20,
                            padding: 18,
                            marginBottom: 16,
                            transition: "all 0.3s ease"
                        }}>
                            {/* ── Card Header ── */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    {/* Avatar */}
                                    <div style={{
                                        width: 48, height: 48, borderRadius: "50%",
                                        background: `linear-gradient(135deg, ${session.chatColor || '#3b82f6'}, ${session.chatColor || '#3b82f6'}88)`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        color: "#fff", fontSize: 16, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                        boxShadow: `0 4px 14px ${session.chatColor || '#3b82f6'}30`, flexShrink: 0
                                    }}>
                                        {session.chatInitials || '?'}
                                    </div>

                                    <div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                            <p style={{ color: "var(--text-main, #0f172a)", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                                {session.chatName || 'Chat Session'}
                                            </p>
                                            <span style={{
                                                fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 8,
                                                background: isActive ? "rgba(34,197,94,0.12)" : "rgba(148,163,184,0.12)",
                                                color: isActive ? "#22c55e" : "#64748b"
                                            }}>
                                                {isActive ? "ACTIVE" : "COMPLETED"}
                                            </span>
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 3 }}>
                                            <Clock size={11} color="var(--text-muted, #94a3b8)" />
                                            <span style={{ color: "var(--text-muted, #94a3b8)", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                                {session.daysEarned}/{session.tenure} chat days
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div style={{ textAlign: "right" }}>
                                    <p style={{ color: "var(--text-muted, #94a3b8)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>Capital</p>
                                    <p style={{ color: "var(--text-main, #0f172a)", fontSize: 16, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                        {session.capital} <span style={{ fontSize: 11, fontWeight: 500 }}>{session.currencyAbbr}</span>
                                    </p>
                                    <p style={{ color: "#3b82f6", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: "2px 0 0" }}>
                                        {session.dailyRoi}% daily
                                    </p>
                                </div>
                            </div>

                            {/* ── Progress Bar ── */}
                            <div style={{ marginBottom: 12 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                    <span style={{ color: "var(--text-muted, #94a3b8)", fontSize: 10, fontFamily: "'DM Sans',sans-serif" }}>Session Progress</span>
                                    <span style={{ color: "#3b82f6", fontSize: 10, fontFamily: "'Syne',sans-serif", fontWeight: 700 }}>{progress}%</span>
                                </div>
                                <div style={{ width: "100%", height: 6, background: "var(--border-color, rgba(59,130,246,0.08))", borderRadius: 3, overflow: "hidden" }}>
                                    <div style={{
                                        height: "100%", width: `${progress}%`,
                                        background: isActive ? "linear-gradient(90deg, #3b82f6, #10b981)" : "#94a3b8",
                                        borderRadius: 3, transition: "width 0.6s ease"
                                    }} />
                                </div>
                            </div>

                            {/* ── Profit Earned Box ── */}
                            <div style={{
                                background: "rgba(34,197,94,0.04)", border: "1px solid rgba(34,197,94,0.08)",
                                borderRadius: 12, padding: "10px 14px", marginBottom: 12,
                                display: "flex", alignItems: "center", justifyContent: "space-between"
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <TrendingUp size={14} color="#22c55e" />
                                    <span style={{ color: "var(--text-muted, #64748b)", fontSize: 12, fontFamily: "'DM Sans',sans-serif" }}>
                                        Accumulated Chat Earnings
                                    </span>
                                </div>
                                <div style={{ textAlign: "right" }}>
                                    <span style={{ color: "#16a34a", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif" }}>
                                        +{session.profit} {session.currencyAbbr}
                                    </span>
                                    <p style={{ margin: 0, color: "var(--text-muted, #94a3b8)", fontSize: 9, fontFamily: "'DM Sans',sans-serif" }}>
                                        {dailyProfit} {session.currencyAbbr}/chat day
                                    </p>
                                </div>
                            </div>

                            {/* ── Action Buttons ── */}
                            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                <button
                                    onClick={() => handleDownloadTranscript(session)}
                                    disabled={downloadingId === session.id}
                                    style={{
                                        flex: 1, height: 38, borderRadius: 10,
                                        border: "1px solid rgba(59,130,246,0.15)",
                                        background: "transparent", color: "#3b82f6",
                                        fontSize: 12, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                        cursor: "pointer", display: "flex", alignItems: "center",
                                        justifyContent: "center", gap: 6, transition: "all 0.2s"
                                    }}
                                >
                                    <Download size={13} />
                                    {downloadingId === session.id ? "Exporting..." : "Download Chat"}
                                </button>

                                <button
                                    onClick={() => toggleLedger(session)}
                                    style={{
                                        flex: 1, height: 38, borderRadius: 10, border: "none",
                                        background: isLedgerOpen ? "rgba(59,130,246,0.12)" : "rgba(59,130,246,0.06)",
                                        color: "#2563eb", fontSize: 12, fontWeight: 700,
                                        fontFamily: "'Syne',sans-serif", cursor: "pointer",
                                        display: "flex", alignItems: "center", justifyContent: "center", gap: 6
                                    }}
                                >
                                    <Layers size={13} />
                                    {isLedgerOpen ? "Hide Ledger" : "Growth Ledger"}
                                    {isLedgerOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                </button>
                            </div>

                            {/* ── Growth Ledger (actual chat payment per day) ── */}
                            {isLedgerOpen && (
                                <div style={{
                                    marginTop: 12, padding: 14, borderRadius: 14,
                                    background: "var(--body-bg, #f8fafc)",
                                    border: "1px solid var(--border-color, rgba(59,130,246,0.06))",
                                    animation: "fadeInUp 0.3s ease"
                                }}>
                                    {/* Header */}
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <div style={{
                                                width: 28, height: 28, borderRadius: 8,
                                                background: "rgba(34,197,94,0.1)",
                                                display: "flex", alignItems: "center", justifyContent: "center"
                                            }}>
                                                <CheckCircle size={13} color="#22c55e" />
                                            </div>
                                            <div>
                                                <p style={{ color: "var(--text-main, #334155)", fontSize: 12, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                                    Confirmed Earnings
                                                </p>
                                                <p style={{ color: "var(--text-muted, #94a3b8)", fontSize: 10, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                                    +{dailyProfit} {session.currencyAbbr} per chat day
                                                </p>
                                            </div>
                                        </div>
                                        {/* <div style={{ textAlign: "right" }}>
                                            <p style={{ margin: 0, color: "var(--text-muted, #94a3b8)", fontSize: 9, fontFamily: "'DM Sans',sans-serif" }}>Total</p>
                                            <p style={{ margin: 0, color: "#16a34a", fontSize: 13, fontWeight: 800, fontFamily: "'Syne',sans-serif" }}>
                                                +{session.profit} <span style={{ fontSize: 9, fontWeight: 500 }}>{session.currencyAbbr}</span>
                                            </p>
                                        </div> */}
                                    </div>

                                    {isLoadingThisLedger ? (
                                        <div style={{ display: "flex", justifyContent: "center", padding: "20px 0" }}>
                                            <div style={{
                                                width: 24, height: 24, borderRadius: "50%",
                                                border: "3px solid rgba(59,130,246,0.15)",
                                                borderTopColor: "#3b82f6",
                                                animation: "spin 0.8s linear infinite"
                                            }} />
                                        </div>
                                    ) : ledger.length === 0 ? (
                                        <div style={{
                                            padding: "18px 0", textAlign: "center",
                                            border: "1px dashed rgba(148,163,184,0.25)",
                                            borderRadius: 10
                                        }}>
                                            <MessageSquare size={20} color="#94a3b8" style={{ opacity: 0.5, marginBottom: 6 }} />
                                            <p style={{ margin: 0, color: "var(--text-muted, #94a3b8)", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                                No earnings yet — start chatting to earn!
                                            </p>
                                        </div>
                                    ) : (
                                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                            {/* Column headers */}
                                            <div style={{
                                                display: "flex", justifyContent: "space-between",
                                                padding: "0 10px 6px",
                                                borderBottom: "1px solid var(--border-color, rgba(59,130,246,0.06))",
                                                marginBottom: 2
                                            }}>
                                                <span style={{ fontSize: 9, fontWeight: 700, color: "var(--text-muted, #94a3b8)", fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>Date</span>
                                                <div style={{ display: "flex", gap: 28 }}>
                                                    <span style={{ fontSize: 9, fontWeight: 700, color: "var(--text-muted, #94a3b8)", fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}>Earned</span>
                                                </div>
                                            </div>

                                            {ledger.map((row, rIdx) => (
                                                <div key={rIdx} style={{
                                                    display: "flex", alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "9px 10px",
                                                    background: "rgba(34,197,94,0.04)",
                                                    border: "1px solid rgba(34,197,94,0.12)",
                                                    borderRadius: 10
                                                }}>
                                                    {/* Left: entry number + date */}
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        <div style={{
                                                            width: 22, height: 22, borderRadius: 6,
                                                            background: "rgba(34,197,94,0.15)",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            flexShrink: 0
                                                        }}>
                                                            <span style={{ fontSize: 9, fontWeight: 800, color: "#16a34a", fontFamily: "'Syne',sans-serif" }}>#{row.entryNum}</span>
                                                        </div>
                                                        <div>
                                                            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-main, #334155)", fontFamily: "'DM Sans',sans-serif" }}>
                                                                {row.date}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {/* Right: earned + running total */}
                                                    <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                                                        <div style={{ textAlign: "right" }}>
                                                            <div style={{ fontSize: 12, fontWeight: 800, color: "#16a34a", fontFamily: "'Syne',sans-serif" }}>
                                                                +{row.earned}
                                                            </div>
                                                            <div style={{ fontSize: 8, color: "var(--text-muted, #94a3b8)", fontFamily: "'DM Sans',sans-serif" }}>
                                                                {session.currencyAbbr}
                                                            </div>
                                                        </div>

                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </>
        );
    }

    // ────────────────────────────────────────────────────────────────────────
    return (
        <SiteWide title={"Transcript"}>
            <Section overlayText={loadingText} loading={loading} title={'Transcript'}>

                <style>{`
                    @keyframes fadeInUp {
                        from { opacity: 0; transform: translateY(8px); }
                        to   { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to   { transform: rotate(360deg); }
                    }
                    @keyframes shimmer {
                        0%   { background-position: -200px 0; }
                        100% { background-position: calc(200px + 100%) 0; }
                    }
                    .ledger-shimmer {
                        background: linear-gradient(90deg, #f0f4f8 25%, #e2e8f0 50%, #f0f4f8 75%);
                        background-size: 200px 100%;
                        animation: shimmer 1.2s infinite;
                    }
                `}</style>

                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                    {/* ── Page Header ── */}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                            <h2 style={{ color: "var(--text-main, #0f172a)", fontSize: 20, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: 0 }}>
                                Chat Transcript
                            </h2>
                            <p style={{ margin: "3px 0 0", color: "var(--text-muted, #94a3b8)", fontSize: 11, fontFamily: "'DM Sans',sans-serif" }}>
                                Your chat session history & payment ledger
                            </p>
                        </div>
                    </div>

                    {/* ── Chart Card ── */}
                    <div style={{
                        background: "var(--card-bg, #ffffff)",
                        border: "1px solid var(--card-border, rgba(59,130,246,0.08))",
                        boxShadow: "var(--card-shadow, 0 4px 18px rgba(59,130,246,0.04))",
                        borderRadius: 20, padding: 16,
                        display: "flex", flexDirection: "column", gap: 12
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                                <span style={{ color: "var(--text-muted, #94a3b8)", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                    Activity & Yield
                                </span>
                                <h3 style={{ color: "var(--text-main, #0f172a)", fontSize: 16, fontWeight: 800, fontFamily: "'Syne',sans-serif", margin: "2px 0 0" }}>
                                    {activeTab === "growth"
                                        ? `Total Earned: ${money(allProfits, type)}`
                                        : "Weekly Chat Activity"
                                    }
                                </h3>
                            </div>
                            <div style={{ display: "flex", gap: 4, background: "rgba(0,0,0,0.03)", padding: 3, borderRadius: 10 }}>
                                {["growth", "activity"].map(tab => (
                                    <button key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        style={{
                                            border: "none", padding: "5px 12px", borderRadius: 8,
                                            fontSize: 10, fontWeight: 700, fontFamily: "'Syne',sans-serif", cursor: "pointer",
                                            background: activeTab === tab ? "var(--card-bg, #fff)" : "transparent",
                                            color: activeTab === tab ? "#2563eb" : "var(--text-muted, #94a3b8)",
                                            boxShadow: activeTab === tab ? "0 2px 6px rgba(0,0,0,0.04)" : "none",
                                            transition: "all 0.2s", textTransform: "capitalize"
                                        }}>
                                        {tab === "growth" ? "Growth" : "Chats"}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* SVG Chart */}
                        <div style={{ position: "relative", width: "100%", height: 160 }}>
                            <svg viewBox="0 0 500 160" width="100%" height="100%" style={{ overflow: "visible" }}>
                                <defs>
                                    <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.28" />
                                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
                                    </linearGradient>
                                    <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.28" />
                                        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                                    </linearGradient>
                                    <filter id="glow2">
                                        <feGaussianBlur stdDeviation="3" result="blur" />
                                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                                    </filter>
                                </defs>
                                <line x1="30" y1="40" x2="470" y2="40" stroke="rgba(59,130,246,0.04)" strokeDasharray="3,3" />
                                <line x1="30" y1="100" x2="470" y2="100" stroke="rgba(59,130,246,0.04)" strokeDasharray="3,3" />
                                <line x1="30" y1="160" x2="470" y2="160" stroke="rgba(59,130,246,0.08)" />
                                <path
                                    d={`M ${points[0].x},160 ${polylineStr} 470,160 Z`}
                                    fill={activeTab === "growth" ? "url(#chartGrad)" : "url(#actGrad)"}
                                    style={{ transition: "all 0.4s ease" }}
                                />
                                <path
                                    d={`M ${polylineStr}`}
                                    fill="none"
                                    stroke={activeTab === "growth" ? "#3b82f6" : "#10b981"}
                                    strokeWidth="3.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    filter="url(#glow2)"
                                    style={{ transition: "all 0.4s ease" }}
                                />
                                {points.map((p, i) => (
                                    <g key={i}>
                                        <circle
                                            cx={p.x} cy={p.y}
                                            r={hoveredPoint === i ? "6" : "4"}
                                            fill={activeTab === "growth" ? "#3b82f6" : "#10b981"}
                                            stroke="#fff" strokeWidth="2.5"
                                            style={{ transition: "all 0.2s ease", cursor: "pointer" }}
                                            onMouseEnter={() => setHoveredPoint(i)}
                                            onMouseLeave={() => setHoveredPoint(null)}
                                        />
                                        <text x={p.x} y="178" textAnchor="middle"
                                            fill="var(--text-muted, #94a3b8)" fontSize="9"
                                            fontFamily="'DM Sans',sans-serif" fontWeight="600">
                                            {p.label}
                                        </text>
                                    </g>
                                ))}
                            </svg>
                            {hoveredPoint !== null && (
                                <div style={{
                                    position: "absolute",
                                    left: `${(points[hoveredPoint].x / 500) * 100}%`,
                                    top: `${(points[hoveredPoint].y / 160) * 100 - 30}%`,
                                    transform: "translate(-50%, -100%)",
                                    background: "rgba(15,23,42,0.95)", color: "#fff",
                                    padding: "6px 10px", borderRadius: 8,
                                    fontSize: 10, fontWeight: 700, fontFamily: "'Syne',sans-serif",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                                    pointerEvents: "none", whiteSpace: "nowrap",
                                    zIndex: 100, display: "flex", flexDirection: "column", gap: 2
                                }}>
                                    <span style={{ opacity: 0.6, fontSize: 8 }}>{points[hoveredPoint].label}</span>
                                    <span>
                                        {activeTab === "growth"
                                            ? `Yield: ${money(points[hoveredPoint].value, type)}`
                                            : `Chats: ${points[hoveredPoint].value} msgs`}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Summary Widgets ── */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                        {[
                            {
                                icon: <MessageSquare size={15} color="#3b82f6" />,
                                bg: "rgba(59,130,246,0.08)", border: "rgba(59,130,246,0.08)",
                                val: chattySessions.length, color: "#3b82f6", label: "Sessions"
                            },
                            {
                                icon: <TrendingUp size={15} color="#22c55e" />,
                                bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.08)",
                                val: activeSessions.length, color: "#22c55e", label: "Active"
                            },
                            {
                                icon: <DollarSign size={15} color="#a855f7" />,
                                bg: "rgba(168,85,247,0.08)", border: "rgba(168,85,247,0.08)",
                                val: money(allProfits, type), color: "#a855f7", label: "Earned"
                            }
                        ].map((w, i) => (
                            <div key={i} style={{
                                background: "var(--card-bg, #ffffff)",
                                border: `1px solid var(--card-border, ${w.border})`,
                                borderRadius: 16, padding: 12,
                                display: "flex", flexDirection: "column", alignItems: "center", gap: 4
                            }}>
                                <div style={{ width: 32, height: 32, borderRadius: 8, background: w.bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    {w.icon}
                                </div>
                                <span style={{ color: w.color, fontSize: 15, fontWeight: 800, fontFamily: "'Syne',sans-serif" }}>{w.val}</span>
                                <span style={{ color: "var(--text-muted, #94a3b8)", fontSize: 8, fontFamily: "'DM Sans',sans-serif", textAlign: "center" }}>{w.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* ── Section Label ── */}
                    <p style={{ color: "var(--text-muted, #64748b)", fontSize: 11, fontFamily: "'DM Sans',sans-serif", textTransform: "uppercase", letterSpacing: "0.06em", margin: "6px 0 0" }}>
                        Chat Session History & Payment Ledger
                    </p>

                    {/* ── Sessions List ── */}
                    {loadingChats ? (
                        <div style={{
                            background: "var(--card-bg, #ffffff)", borderRadius: 20,
                            border: "1px solid var(--card-border, rgba(59,130,246,0.08))",
                            padding: 40, display: "flex", flexDirection: "column", alignItems: "center", gap: 12
                        }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: "50%",
                                border: "3px solid rgba(59,130,246,0.15)", borderTopColor: "#3b82f6",
                                animation: "spin 0.8s linear infinite"
                            }} />
                            <p style={{ color: "var(--text-muted, #94a3b8)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                Loading chat sessions...
                            </p>
                        </div>
                    ) : chattySessions.length > 0 ? (
                        <PaginatedItems Template={SessionCardTemplate} arrayOfDataToDisplay={chattySessions} itemsPerPage={4} />
                    ) : (
                        <div style={{
                            background: "var(--card-bg, #ffffff)", borderRadius: 16,
                            border: "1px solid var(--card-border, rgba(59,130,246,0.08))",
                            padding: 40, display: "flex", flexDirection: "column", alignItems: "center", gap: 12
                        }}>
                            <div style={{
                                width: 56, height: 56, borderRadius: "50%",
                                background: "rgba(59,130,246,0.06)",
                                border: "1px solid rgba(59,130,246,0.12)",
                                display: "flex", alignItems: "center", justifyContent: "center"
                            }}>
                                <MessageSquare size={24} color="#3b82f6" style={{ opacity: 0.5 }} />
                            </div>
                            <div style={{ textAlign: "center" }}>
                                <p style={{ color: "var(--text-main, #0f172a)", fontSize: 15, fontWeight: 700, fontFamily: "'Syne',sans-serif", margin: "0 0 6px" }}>
                                    No Chat Sessions Yet
                                </p>
                                <p style={{ color: "var(--text-muted, #94a3b8)", fontSize: 12, fontFamily: "'DM Sans',sans-serif", margin: 0 }}>
                                    Start a chat session and begin earning daily.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </Section>
        </SiteWide>
    );
}

export default Transcript;
